import { apiErrorEnvelopeSchema } from "@sage/shared";

export const ALLOWED_FILE_EXTENSIONS = [
  ".exe", ".dll", ".sys",
  ".elf", ".so",
  ".macho", ".dylib",
  ".pdf",
  ".zip", ".rar", ".7z",
  ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx",
  ".js", ".vbs", ".ps1", ".bat", ".sh", ".py",
  ".rtf"
] as const;

export const MAX_FILE_SIZE_BYTES = 20_971_520; // 20 MB

export type ValidationResult =
  | { valid: true }
  | { valid: false; reason: string };

export type UploadResult =
  | { ok: true; sampleId: string }
  | { ok: false; error: string };

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

/**
 * Compute SHA-256 hash of a file using the Web Crypto API.
 */
export async function computeSHA256(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Validate a file for upload.
 */
export function validateUploadFile(file: File): ValidationResult {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return { valid: false, reason: `File exceeds 20 MB limit (${formatSize(file.size)}).` };
  }

  const name = file.name.toLowerCase();
  const hasAllowedExtension = ALLOWED_FILE_EXTENSIONS.some((ext) => name.endsWith(ext));

  if (!hasAllowedExtension) {
    return { valid: false, reason: "File type is not in the allowed list (PE, ELF, Mach-O, PDF, archives, Office, scripts)." };
  }

  return { valid: true };
}

/**
 * Upload a file with its pre-computed SHA-256 hash.
 */
export async function uploadSample(file: File, sha256: string, token: string | null): Promise<UploadResult> {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("sha256", sha256);

    const response = await fetch(`${API_BASE_URL}/v1/samples/upload`, {
      method: "POST",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    });

    const body: unknown = await response.json().catch(() => null);

    if (!response.ok) {
      const parsed = apiErrorEnvelopeSchema.safeParse(body);
      const message = parsed.success ? parsed.data.error.message : "Upload failed.";
      return { ok: false, error: message };
    }

    if (isRecord(body) && body.ok === true && isRecord(body.data)) {
      const sampleId = typeof body.data.id === "string" ? body.data.id : "unknown";
      return { ok: true, sampleId };
    }

    return { ok: true, sampleId: "unknown" };
  } catch {
    return { ok: false, error: "Unable to reach the upload API. Check the network or API base URL." };
  }
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
